import { defaultAllSelection } from "services/constants/selections";
import React, { useEffect, useState } from "react";
import { Box, Stack, Typography, Divider, CircularProgress } from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import sessionHandling from "services/utils/notificationUtils";
import { checkViewerPermission } from "services/utils/HelperFunction";
import { designWeights } from "services/constants/designTokens";
import { dashboard } from "../dashboardStyles";
import { formatShiftClock } from "../../MyShift/shiftHelpers";

// Today's Attendance + Today's Shift KPI tiles. Both are driven by the SAME
// `checkinfilter` row the My Zone > Status screen (AllStatus.js) already uses,
// filtered to the logged-in user, so there is a single fetch behind the two
// tiles and their values can't disagree with the Status screen.
const cleanValue = (v) => (v && v !== "null" ? v : null);

const normalizeSpecialDayLabel = (value) => {
  if (!value) return value;
  const text = String(value).trim();
  if (/^(weekoff|weekly off|week-off)$/i.test(text)) return "Week Off";
  return text;
};

const isOpenBreak = (attendanceRow) => {
  const breaks = Array.isArray(attendanceRow?.break_details)
    ? attendanceRow.break_details
    : [];
  // checkinfilter's break entries use break_out_formatted/break_in_formatted
  // rather than plain break_out/break_in, and the array isn't guaranteed to
  // be chronological, so look for any break that was started but never
  // closed rather than trusting array position.
  return breaks.some((brk) => {
    const breakOut = brk?.break_out || brk?.break_out_formatted;
    const breakIn = brk?.break_in || brk?.break_in_formatted;
    return Boolean(breakOut && !breakIn);
  });
};

const resolveAttendanceStatus = (attendanceRow) => {
  // attendance_status is checkinfilter's own purpose-built status field
  // (e.g. "Out") and is the source of truth whenever the backend sets it -
  // same fix as My Attendance's normalizeTodayAttendanceRow. The live
  // isOpenBreak/checkedIn heuristics below only step in as a fallback for
  // when the backend hasn't provided one.
  const backendStatus = cleanValue(attendanceRow?.attendance_status);
  const backendLabel = normalizeSpecialDayLabel(
    backendStatus ||
      cleanValue(attendanceRow?.card_display_status) ||
      cleanValue(attendanceRow?.type) ||
      cleanValue(attendanceRow?.current_location_tag) ||
      "-",
  );
  const backendColor =
    attendanceRow?.card_display_status_color ||
    attendanceRow?.status_color ||
    dashboard.colors.green;
  const currentTag = cleanValue(attendanceRow?.current_location_tag);
  const normalizedTag = (currentTag || "").toLowerCase();
  const checkedIn = Boolean(cleanValue(attendanceRow?.check_in_time_formatted));
  const checkedOut = Boolean(cleanValue(attendanceRow?.check_out_time_formatted));

  if (backendStatus) {
    return { label: backendLabel, color: backendColor };
  }

  if (isOpenBreak(attendanceRow) || normalizedTag.includes("break")) {
    return {
      label: currentTag || "On Break",
      color: dashboard.colors.amber,
    };
  }

  if (["Leave", "Week Off", "Holiday"].includes(backendLabel)) {
    return { label: backendLabel, color: backendColor };
  }

  if (checkedIn && !checkedOut) {
    return {
      label: currentTag || "Checked In",
      color: dashboard.colors.green,
    };
  }

  return { label: backendLabel, color: backendColor };
};

export const AttendanceShiftKpis = () => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, token, userDetails, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  const history = useHistory();
  const [row, setRow] = useState(null);
  const [todayShift, setTodayShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const date = new Date().toISOString().split("T")[0];

  // These two tiles link to two different screens despite sharing one fetch
  // -- "Today's Attendance" opens My Attendance, "Today's Shift" opens
  // Shift -- so each needs its own module's viewer access, not one shared
  // check. Previously both always showed regardless of either.
  const hasAttendanceAccess = checkViewerPermission(
    userScreenAccess?.module_details,
    "My Attendance",
    "My Attendance",
  );
  const hasShiftAccess = checkViewerPermission(
    userScreenAccess?.module_details,
    "Shift",
    "Shift",
  );

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const headers = {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        };
        const [attendanceResponse, shiftResponse] = await Promise.all([
          fetch(
            `${domain}checkinfilter/?department_id=${userDetails?.department_id || defaultAllSelection}&date=${date}&selected_user_id=${userDetails?.id}&company_id=${requestCompanyId}`,
            { headers },
          ),
          hasShiftAccess
            ? fetch(
                `${domain}daily_user_shift_assignments/?selected_user_id=${userDetails?.id}&from_date=${date}&to_date=${date}&company_id=${requestCompanyId}`,
                { headers },
              )
            : Promise.resolve(null),
        ]);

        const res = await attendanceResponse.json();
        if (attendanceResponse.ok) {
          const mine =
            res?.user_status_details?.find(
              (item) => String(item?.user_id) === String(userDetails?.id),
            ) || res?.user_status_details?.[0];
          if (!cancelled) setRow(mine || null);
        } else if (attendanceResponse.status === 409) {
          sessionHandling();
        }

        if (shiftResponse) {
          const shiftRes = await shiftResponse.json();
          if (shiftResponse.ok) {
            const shifts = Array.isArray(shiftRes?.data)
              ? shiftRes.data.flatMap((item) => item?.shifts || [])
              : [];
            if (!cancelled) setTodayShift(shifts[0] || null);
          } else if (shiftResponse.status === 409) {
            sessionHandling();
          }
        }
      } catch (error) {
        console.error("[AttendanceShiftKpis] operation failed:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (userDetails?.id && (hasAttendanceAccess || hasShiftAccess)) {
      fetchData();
    } else {
      setLoading(false);
    }
    return () => {
      cancelled = true;
    };
  }, [
    domain,
    token,
    date,
    userDetails?.id,
    userDetails?.department_id,
    hasAttendanceAccess,
    hasShiftAccess, requestCompanyId
  ]);

  const status = resolveAttendanceStatus(row);
  const checkIn = cleanValue(row?.check_in_time_formatted);
  const checkOut = cleanValue(row?.check_out_time_formatted);
  const worked =
    row?.total_work_time && row.total_work_time !== "00:00"
      ? row.total_work_time
      : "00:00";
  const rowShiftName = normalizeSpecialDayLabel(cleanValue(row?.shift_name));
  const scheduleShiftName = normalizeSpecialDayLabel(cleanValue(todayShift?.shift_name));
  const shiftName =
    (rowShiftName && rowShiftName !== "Unassigned" ? rowShiftName : null) ||
    scheduleShiftName ||
    "-";
  const rowShiftHours = cleanValue(row?.shift_hours);
  const scheduleShiftHours =
    todayShift?.shift_start_time && todayShift?.shift_end_time
      ? `${formatShiftClock(todayShift.shift_start_time)} - ${formatShiftClock(
          todayShift.shift_end_time,
        )}`
      : "";
  const shiftHours = rowShiftHours && rowShiftHours !== "00:00"
    ? `${rowShiftHours} hrs`
    : scheduleShiftHours;

  const KpiShell = ({ icon, iconColor, title, chip, children, onView, viewLabel }) => (
    <Box sx={dashboard.cardSx}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={dashboard.iconWrap(iconColor)}>{icon}</Box>
          <Typography fontWeight={designWeights.semibold} sx={{ color: dashboard.colors.subText }}>
            {title}
          </Typography>
        </Stack>
        {chip}
      </Stack>
      {loading ? (
        <CircularProgress size={20} />
      ) : (
        children
      )}
      <Divider sx={{ my: 1.5 }} />
      <Typography sx={dashboard.linkSx} onClick={onView}>
        {viewLabel} -&gt;
      </Typography>
    </Box>
  );

  return (
    <>
      {hasAttendanceAccess ? (
        <KpiShell
          icon={<LoginIcon fontSize="small" />}
          iconColor={dashboard.colors.green}
          title="Today's Attendance"
          chip={
            <Box
              sx={{
                px: 1.2,
                py: 0.3,
                borderRadius: 5,
                fontSize: 11,
                fontWeight: designWeights.semibold,
                color: status.color,
                bgcolor: `${status.color}1A`,
              }}
            >
              {status.label}
            </Box>
          }
          onView={() => history.push("/admin/myzone/myworkinghours")}
          viewLabel="View My Attendance"
        >
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Box>
              <Typography variant="h6" fontWeight={designWeights.heading} sx={{ color: dashboard.colors.text }}>
                {checkIn || "--:--"}
              </Typography>
              <Typography variant="caption" sx={{ color: dashboard.colors.subText }}>
                Check In
              </Typography>
            </Box>
            <Box textAlign="right">
              <Typography variant="h6" fontWeight={designWeights.heading} sx={{ color: dashboard.colors.text }}>
                {checkOut || "--:--"}
              </Typography>
              <Typography variant="caption" sx={{ color: dashboard.colors.subText }}>
                {worked} Worked
              </Typography>
            </Box>
          </Stack>
        </KpiShell>
      ) : null}

      {hasShiftAccess ? (
        <KpiShell
          icon={<CalendarMonthIcon fontSize="small" />}
          iconColor={dashboard.colors.blue}
          title="Today's Shift"
          onView={() => history.push("/admin/myzone/myshift")}
          viewLabel="View Shift Details"
        >
          <Typography variant="h6" fontWeight={designWeights.heading} sx={{ color: dashboard.colors.text }}>
            {shiftName}
          </Typography>
          <Typography variant="caption" sx={{ color: dashboard.colors.subText }}>
            {shiftHours || "No shift timing"}
          </Typography>
        </KpiShell>
      ) : null}
    </>
  );
};
